import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Form,
  Spinner,
  Alert,
  Badge,
  Card,
} from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import { hiringReferralsApiService, hiringDirectionsApiService } from '../../services/apiServices';
import {
  ReferralApplicationResponse,
  ReferralApplicationStatus,
  RecruitingDirectionDto,
} from '../../api-client';
import { getReferralApplicationStatusLabel } from '../utils/statusConverters';
import { SmartPagination } from '../../components/SmartPagination';
import ReferralCreateForm from '../components/ReferralCreateForm';

const MyHiringReferralsPage: React.FC = () => {
  const [applications, setApplications] = useState<ReferralApplicationResponse[]>([]);
  const [directions, setDirections] = useState<RecruitingDirectionDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCreateForm, setShowCreateForm] = useState<boolean>(false);
  const [createSuccess, setCreateSuccess] = useState<boolean>(false);

  const statusFilter = searchParams.get('status') || '';
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const limit = 10;

  useEffect(() => {
    const loadDirections = async () => {
      try {
        const response = await hiringDirectionsApiService.getDirections();
        setDirections(response.data || []);
      } catch (err: any) {
        console.error('Ошибка при загрузке направлений:', err);
      }
    };
    loadDirections();
  }, []);

  const loadApplications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await hiringReferralsApiService.getMyApplications(
        statusFilter || undefined,
        limit,
        (currentPage - 1) * limit
      );
      setApplications(response.data || []);
    } catch (err: any) {
      console.error('Ошибка при загрузке моих заявок реферальной программы:', err);
      setError(err.message || 'Ошибка при загрузке заявок');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, currentPage, limit]);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const handleStatusChange = useCallback(
    (value: string) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (value) {
          params.set('status', value);
        } else {
          params.delete('status');
        }
        params.set('page', '1');
        return params;
      });
    },
    [setSearchParams]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set('page', newPage.toString());
        return params;
      });
    },
    [setSearchParams]
  );

  const handleCreateSubmit = async (
    directionId: string,
    candidateName: string,
    comment?: string | null,
    resumeType?: any,
    resumeFileUrl?: string | null,
    resumeFileName?: string | null,
    resumeUrl?: string | null
  ) => {
    await hiringReferralsApiService.createMyApplication(
      directionId,
      candidateName,
      comment,
      resumeType,
      resumeFileUrl,
      resumeFileName,
      resumeUrl
    );
    setShowCreateForm(false);
    setCreateSuccess(true);
    loadApplications();
    setTimeout(() => setCreateSuccess(false), 3000);
  };

  const handleUploadResume = async (file: File) => {
    const response = await hiringReferralsApiService.uploadResume(file);
    return {
      url: response.data.url,
      fileName: response.data.file_name,
    };
  };

  const handleResetFilters = useCallback(() => {
    setSearchParams({}, { replace: false });
  }, [setSearchParams]);

  const getStatusBadgeVariant = (status: ReferralApplicationStatus) => {
    switch (status) {
      case ReferralApplicationStatus.PENDING:
        return 'warning';
      case ReferralApplicationStatus.REVIEWING:
        return 'info';
      case ReferralApplicationStatus.ACCEPTED:
        return 'success';
      case ReferralApplicationStatus.REJECTED:
        return 'danger';
      default:
        return 'secondary';
    }
  };

  return (
    <Container fluid>
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Мои заявки на реферальную программу</h2>
            <Button
              variant={showCreateForm ? 'outline-secondary' : 'primary'}
              onClick={() => setShowCreateForm(!showCreateForm)}
            >
              {showCreateForm ? 'Скрыть форму' : 'Создать заявку'}
            </Button>
          </div>

          {showCreateForm && (
            <Card className="mb-4">
              <Card.Body>
                <h5>Новая заявка</h5>
                <ReferralCreateForm
                  directions={directions}
                  onSubmit={handleCreateSubmit}
                  onCancel={() => setShowCreateForm(false)}
                  onUploadResume={handleUploadResume}
                />
              </Card.Body>
            </Card>
          )}

          {createSuccess && <Alert variant="success">Заявка успешно создана!</Alert>}

          {/* Фильтры */}
          <div className="row mb-4">
            <div className="col-md-3 mb-2">
              <Form.Label>Статус</Form.Label>
              <Form.Select
                value={statusFilter}
                onChange={(e) => handleStatusChange(e.target.value)}
              >
                <option value="">Все</option>
                <option value="pending">Ожидает обработки</option>
                <option value="reviewing">На рассмотрении</option>
                <option value="accepted">Принято</option>
                <option value="rejected">Отклонено</option>
              </Form.Select>
            </div>
            <div className="col-md-2 mb-2 d-flex align-items-end">
              <Button variant="outline-secondary" className="w-100" onClick={handleResetFilters}>
                Сбросить
              </Button>
            </div>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}

          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" />
              <p className="mt-2">Загрузка заявок...</p>
            </div>
          ) : (
            <>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Кандидат</th>
                    <th>Направление</th>
                    <th>Тип резюме</th>
                    <th>Статус</th>
                    <th>Дата создания</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {applications && applications.length > 0 ? (
                    applications.map((app) => {
                      const directionName = app.direction?.name || '—';
                      return (
                        <tr key={app.id}>
                          <td style={{ wordBreak: 'break-word', maxWidth: '200px' }}>
                            {app.candidate_name || '—'}
                          </td>
                          <td>{directionName}</td>
                          <td>
                            <Badge bg="secondary">
                              {app.resume_type === 'file' ? 'Файл' : 'URL'}
                            </Badge>
                          </td>
                          <td>
                            <Badge bg={getStatusBadgeVariant(app.status)}>
                              {getReferralApplicationStatusLabel(app.status)}
                            </Badge>
                          </td>
                          <td>{new Date(app.created_at).toLocaleDateString()}</td>
                          <td>
                            <Link to={`/hiring/my/${app.id}`}>
                              <Button variant="outline-primary" size="sm">
                                Подробнее
                              </Button>
                            </Link>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center">
                        У вас пока нет заявок
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>

              <SmartPagination
                currentPage={currentPage}
                limit={limit}
                currentItemsCount={applications.length}
                onPageChange={handlePageChange}
                siblingCount={1}
              />
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default MyHiringReferralsPage;
